import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { ApiError, api, unwrap } from '../api/client.js';
import { FixContentView } from '../components/FixPanel.js';
import { Card, Loading, Notice } from '../components/ui.js';
import { formatDate } from '../i18n.js';

/**
 * Ready-made fixes the site owner sent to their web designer (T-72): no sign-in, valid for 30 days,
 * in the owner's language. Shows each fix with the values to copy and whether it is done.
 */
export function SharedFixes() {
  const { token = '' } = useParams();
  const { i18n } = useTranslation();
  const page = useQuery({
    queryKey: ['public-fixes', token],
    queryFn: () => unwrap(api.GET('/public/fixes/{token}', { params: { path: { token } } })),
    retry: false,
  });
  const t = i18n.getFixedT(page.data?.locale ?? i18n.language);

  if (page.isPending) return <Loading />;
  if (page.isError) {
    const expired = page.error instanceof ApiError && page.error.code === 'TOKEN_EXPIRED';
    return (
      <Notice kind="error">{expired ? t('sharedFixes.expired') : t('sharedFixes.notFound')}</Notice>
    );
  }
  const d = page.data;
  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-bold">{t('sharedFixes.title', { site: d.siteName })}</h1>
        <p className="text-slate-700">
          {t('sharedFixes.intro', { account: d.accountName, host: d.host })}
        </p>
        <p className="text-sm text-slate-600">
          {t('sharedFixes.valid', { date: formatDate(d.expiresAt, d.locale) })}
        </p>
      </header>
      {d.message ? (
        <Card>
          <h2 className="font-semibold">{t('sharedFixes.message', { account: d.accountName })}</h2>
          <p className="whitespace-pre-wrap">{d.message}</p>
        </Card>
      ) : null}
      {d.items.length === 0 ? <Notice kind="info">{t('sharedFixes.empty')}</Notice> : null}
      <ol className="flex flex-col gap-4">
        {d.items.map((item, i) => (
          <li key={item.ruleCode}>
            <Card>
              <h2 className="mb-2 flex flex-wrap items-center gap-2 text-lg font-semibold">
                <span>
                  {i + 1}. {item.title}
                </span>
                {item.done ? (
                  <span className="rounded bg-emerald-100 px-2 py-0.5 text-sm text-emerald-900">
                    {t('sharedFixes.done')}
                  </span>
                ) : null}
              </h2>
              <FixContentView fix={item.content} />
            </Card>
          </li>
        ))}
      </ol>
    </div>
  );
}
